// File: apps/web/src/features/import/ProposalsPanel.tsx
//
// AI-proposed work items review queue. Rendered below or instead of
// StagedList depending on the active tab in ImportPage.
//
// Visual structure:
//
//   ┌─ GlassPanel ─────────────────────────────────────────────────────┐
//   │ PROPOSED WORK ITEMS         [Accepted] [Rejected] [All] refresh │
//   │                                                                  │
//   │ ┌─ Proposal row (collapsed) ─────────────────────────────────┐  │
//   │ │ > Foundry Anthropic Passthrough Integration   ▸ 7 convs   │  │
//   │ │   cluster_key: foundry-anthropic-routing                  │  │
//   │ │   Project: ForgeTrack · Phase: AI Pipeline                │  │
//   │ │   avg confidence: 82%  [Accept] [Reject] [Edit] [Merge]   │  │
//   │ └────────────────────────────────────────────────────────────┘  │
//   │                                                                  │
//   │ ┌─ Proposal row (expanded) ──────────────────────────────────┐  │
//   │ │ ▾ Production Hardening — Dashboard TVFs, Billing Config   │  │
//   │ │   ...                                                       │  │
//   │ │   Contributing conversations:                              │  │
//   │ │     • Some issues (40 msgs / 69 min) — 0.82 confidence     │  │
//   │ │     • Dashboard errors (...)                                │  │
//   │ │   [Accept] [Reject] [Edit] [Merge into...]                 │  │
//   │ └────────────────────────────────────────────────────────────┘  │
//   └──────────────────────────────────────────────────────────────────┘
//
// Actions use createPortal-based modals matching the pattern in
// StagedList.ReclassifyButton and StagedDetail.RDQualificationCard.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';
import { GlassPanel, SectionLabel, EmptyState, LoadingShimmer } from '../../components/engine/EnginePageShell';
import { Tooltip, TooltipTitle, TooltipBody } from '../../components/engine/Tooltip';
import { useProjects } from '../../lib/api/projects';
import {
  useProposals,
  useProposalDetail,
  useAcceptProposal,
  useRejectProposal,
  useEditProposal,
  useMergeProposal,
} from '../../lib/api/import';
import type {
  ProposalStatus,
  ProposalSummary,
  ProposalContributionItem,
} from '../../lib/api/types-import';

interface ProposalsPanelProps {
  batchFilter?: string;
  refreshKey?:  number;
  onMutated?:   () => void;
}

const STATUS_FILTERS: { value: ProposalStatus | 'All'; label: string; color: string; tooltip: ReactNode }[] = [
  {
    value: 'Pending',
    label: 'Pending',
    color: C.amber,
    tooltip: (
      <>
        <TooltipTitle>Pending proposals</TooltipTitle>
        <TooltipBody>
          AI-proposed work items awaiting human review. Accept them to
          create a real catalog entry, reject them to send contributing
          conversations back to the Unmapped queue, or merge near-duplicate
          proposals together.
        </TooltipBody>
      </>
    ),
  },
  {
    value: 'Accepted',
    label: 'Accepted',
    color: C.neon,
    tooltip: (
      <>
        <TooltipTitle>Accepted proposals</TooltipTitle>
        <TooltipBody>
          Proposals you accepted. Each has a real WorkItem — the link in
          the detail view takes you there. Contributing conversations now
          map to that work item for auto-commit.
        </TooltipBody>
      </>
    ),
  },
  {
    value: 'Rejected',
    label: 'Rejected',
    color: C.coral,
    tooltip: (
      <>
        <TooltipTitle>Rejected proposals</TooltipTitle>
        <TooltipBody>
          Proposals you rejected. Contributing conversations did NOT get
          a work item mapping — they are still in the Unmapped queue of
          the Conversations tab for manual handling.
        </TooltipBody>
      </>
    ),
  },
  {
    value: 'Merged',
    label: 'Merged',
    color: C.violet,
    tooltip: (
      <>
        <TooltipTitle>Merged proposals</TooltipTitle>
        <TooltipBody>
          Proposals whose contributions were moved into another proposal.
          Kept for audit — shows the merge target in the detail view.
        </TooltipBody>
      </>
    ),
  },
  {
    value: 'All',
    label: 'All',
    color: C.cyan,
    tooltip: (
      <>
        <TooltipTitle>All proposals</TooltipTitle>
        <TooltipBody>Every proposal regardless of status.</TooltipBody>
      </>
    ),
  },
];

export function ProposalsPanel({ batchFilter, refreshKey, onMutated }: ProposalsPanelProps) {
  const [statusFilter, setStatusFilter] = useState<ProposalStatus | 'All'>('Pending');
  const [expandedId,   setExpandedId]   = useState<number | null>(null);

  // Modal state — only one modal open at a time across the whole panel.
  type ModalKind = 'accept' | 'reject' | 'edit' | 'merge';
  const [modal, setModal] = useState<{ kind: ModalKind; proposal: ProposalSummary } | null>(null);

  const { data, loading, error, refetch } =
    useProposals({ batchId: batchFilter, status: statusFilter });

  // Refresh from parent (after an import completes, after a classification
  // job finishes, etc.). Also clears the modal so stale state doesn't leak
  // across refreshes.
  useMemoRefetch(refreshKey, refetch);

  const handleRefresh = () => { setModal(null); refetch(); };
  const handleMutated = () => { setModal(null); refetch(); onMutated?.(); };

  // Project list for Accept/Edit modal dropdowns
  const { data: projectsData } = useProjects();
  const projects = (projectsData ?? []).filter(p => !p.isArchived);

  return (
    <GlassPanel accent={C.violet}>
      <div style={{ padding: 24 }}>
        {/* Header */}
        <div style={{
          display:        'flex',
          justifyContent: 'space-between',
          alignItems:     'center',
          marginBottom:   16,
        }}>
          <SectionLabel color={C.violet}>PROPOSED WORK ITEMS</SectionLabel>
          <button
            onClick={handleRefresh}
            style={refreshButtonStyle}
            disabled={loading}
            title="Refresh"
          >
            {loading ? '◌' : '↻'}
          </button>
        </div>

        {/* Status filter pills */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {STATUS_FILTERS.map(f => (
            <Tooltip key={f.value} content={f.tooltip} placement="bottom" maxWidth={300}>
              <button
                onClick={() => { setStatusFilter(f.value); setExpandedId(null); }}
                style={filterPillStyle(statusFilter === f.value, f.color)}
              >
                {f.label}
                {statusFilter === f.value && data && (
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>{data.total}</span>
                )}
              </button>
            </Tooltip>
          ))}
          {batchFilter && (
            <span style={batchPillStyle}>
              batch: <code style={{ fontFamily: F.mono, marginLeft: 4 }}>{batchFilter}</code>
            </span>
          )}
        </div>

        {/* Body */}
        {loading && !data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <LoadingShimmer height={72} />
            <LoadingShimmer height={72} />
            <LoadingShimmer height={72} />
          </div>
        )}

        {error && (
          <div style={errorBoxStyle}>
            Failed to load proposals: {error.message}
          </div>
        )}

        {data && data.items.length === 0 && (
          <EmptyState
            icon="💡"
            title={
              statusFilter === 'Pending'
                ? 'No pending proposals'
                : `No ${statusFilter.toLowerCase()} proposals`
            }
            subtitle={
              statusFilter === 'Pending'
                ? 'The classifier will surface new proposals when it finds conversations that deserve their own work item but have no catalog match.'
                : 'Try a different status filter or a different batch.'
            }
          />
        )}

        {data && data.items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.items.map(p => (
              <ProposalRow
                key={p.id}
                proposal={p}
                expanded={expandedId === p.id}
                onToggleExpand={() => setExpandedId(expandedId === p.id ? null : p.id)}
                onAccept={() => setModal({ kind: 'accept', proposal: p })}
                onReject={() => setModal({ kind: 'reject', proposal: p })}
                onEdit={() => setModal({ kind: 'edit', proposal: p })}
                onMerge={() => setModal({ kind: 'merge', proposal: p })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.kind === 'accept' && (
        <AcceptProposalModal
          proposal={modal.proposal}
          projects={projects}
          onClose={() => setModal(null)}
          onDone={handleMutated}
        />
      )}
      {modal?.kind === 'reject' && (
        <RejectProposalModal
          proposal={modal.proposal}
          onClose={() => setModal(null)}
          onDone={handleMutated}
        />
      )}
      {modal?.kind === 'edit' && (
        <EditProposalModal
          proposal={modal.proposal}
          projects={projects}
          onClose={() => setModal(null)}
          onDone={handleMutated}
        />
      )}
      {modal?.kind === 'merge' && data && (
        <MergeProposalModal
          source={modal.proposal}
          candidates={data.items.filter(p => p.id !== modal.proposal.id && p.status === 'Pending')}
          onClose={() => setModal(null)}
          onDone={handleMutated}
        />
      )}
    </GlassPanel>
  );
}

// Trigger refetch whenever the parent's refreshKey changes. We deliberately
// don't put `fn` in the deps array — `refetch` is recreated on every render
// inside useProposals (useCallback re-runs when the request object changes),
// and including it would turn every parent re-render into an infinite
// refetch loop. Key-based dependency is the correct signal.
function useMemoRefetch(key: number | undefined, fn: () => Promise<void>) {
  useEffect(() => {
    if (key !== undefined) void fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

// ─── Proposal row (collapsed + expanded) ─────────────────────────────────

function ProposalRow({
  proposal,
  expanded,
  onToggleExpand,
  onAccept,
  onReject,
  onEdit,
  onMerge,
}: {
  proposal:       ProposalSummary;
  expanded:       boolean;
  onToggleExpand: () => void;
  onAccept:       () => void;
  onReject:       () => void;
  onEdit:         () => void;
  onMerge:        () => void;
}) {
  const isPending = proposal.status === 'Pending';

  return (
    <div style={{
      border:       `1px solid ${statusBorderColor(proposal.status)}`,
      borderRadius: 8,
      background:   C.surface,
      overflow:     'hidden',
    }}>
      {/* Header row */}
      <button
        onClick={onToggleExpand}
        style={headerButtonStyle}
      >
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 14, fontWeight: 600, color: C.text, fontFamily: F.display,
          }}>
            <span style={{ color: C.muted, fontFamily: F.mono, fontSize: 11 }}>
              {expanded ? '▾' : '▸'}
            </span>
            <span style={{
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
            }}>
              {proposal.title}
            </span>
            <StatusBadge status={proposal.status} />
          </div>
          <div style={{
            fontSize: 11, color: C.dim, marginTop: 4, fontFamily: F.mono,
            display: 'flex', gap: 12, flexWrap: 'wrap',
          }}>
            <span>{proposal.clusterKey}</span>
            {proposal.suggestedProjectName && (
              <span style={{ color: C.cyan }}>· {proposal.suggestedProjectName}</span>
            )}
            {proposal.suggestedPhaseName && (
              <span>· phase: {proposal.suggestedPhaseName}</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <ConfidenceBar value={proposal.averageContributionConfidence} />
          <span style={contributionPillStyle(proposal.contributionCount)}>
            {proposal.contributionCount} {proposal.contributionCount === 1 ? 'convo' : 'convos'}
          </span>
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '0 18px 18px 18px', borderTop: `1px solid ${C.faint}` }}>
          <ProposalExpandedBody proposalId={proposal.id} />
          {isPending && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button onClick={onAccept} style={primaryActionButtonStyle(C.neon)}>
                ✓ Accept & create work item
              </button>
              <button onClick={onEdit} style={secondaryActionButtonStyle(C.cyan)}>
                ✎ Edit
              </button>
              <button onClick={onMerge} style={secondaryActionButtonStyle(C.violet)}>
                ⇌ Merge into…
              </button>
              <button onClick={onReject} style={secondaryActionButtonStyle(C.coral)}>
                ✗ Reject
              </button>
            </div>
          )}
          {!isPending && proposal.status === 'Accepted' && proposal.acceptedWorkItemId && (
            <div style={{ marginTop: 14, fontSize: 12, color: C.neon, fontFamily: F.mono }}>
              ✓ Accepted — WorkItem #{proposal.acceptedWorkItemId} was created.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Expanded body: fetches full detail + renders contributions ─────────

function ProposalExpandedBody({ proposalId }: { proposalId: number }) {
  const { data, loading, error } = useProposalDetail(proposalId);

  if (loading && !data) return (
    <div style={{ padding: '12px 0' }}>
      <LoadingShimmer height={20} />
      <div style={{ height: 8 }} />
      <LoadingShimmer height={20} />
    </div>
  );
  if (error) return (
    <div style={{ padding: '12px 0', fontSize: 12, color: C.coral }}>
      Failed to load detail: {error.message}
    </div>
  );
  if (!data) return null;

  return (
    <div style={{ paddingTop: 14 }}>
      {data.description && (
        <div style={{
          fontSize: 12, color: C.textSoft, lineHeight: 1.55, marginBottom: 14,
          fontFamily: F.display,
        }}>
          {data.description}
        </div>
      )}

      <div style={{
        fontSize: 10, fontFamily: F.mono, color: C.dim, letterSpacing: '0.08em',
        textTransform: 'uppercase', marginBottom: 8,
      }}>
        Contributing conversations ({data.contributions.length})
      </div>

      <div style={{
        display: 'flex', flexDirection: 'column',
        border: `1px solid ${C.faint}`, borderRadius: 6, overflow: 'hidden',
      }}>
        {data.contributions.map((c, i) => (
          <ContributionRow key={c.importedConversationId} c={c} isLast={i === data.contributions.length - 1} />
        ))}
      </div>

      {data.status === 'Rejected' && data.rejectionReason && (
        <div style={{ marginTop: 12, fontSize: 12, color: C.coral, fontFamily: F.display }}>
          <strong>Rejection reason:</strong> {data.rejectionReason}
        </div>
      )}
      {data.status === 'Merged' && data.mergedIntoProposedWorkItemId && (
        <div style={{ marginTop: 12, fontSize: 12, color: C.violet, fontFamily: F.display }}>
          <strong>Merged into proposal #{data.mergedIntoProposedWorkItemId}.</strong>
        </div>
      )}
    </div>
  );
}

function ContributionRow({ c, isLast }: { c: ProposalContributionItem; isLast: boolean }) {
  return (
    <div style={{
      display: 'flex', gap: 12, padding: '10px 14px',
      borderBottom: isLast ? 'none' : `1px solid ${C.faint}`,
      alignItems: 'center',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, color: C.text, fontFamily: F.display,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {c.conversationTitle}
        </div>
        <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginTop: 2 }}>
          {c.messageCount} msgs · {c.estimatedDurationMinutes}m · #{c.importedConversationId}
        </div>
        {c.reasoning && (
          <div style={{
            fontSize: 11, color: C.muted, fontFamily: F.display, marginTop: 4,
            fontStyle: 'italic',
          }}>
            {c.reasoning}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        <ConfidenceBar value={c.confidence} small />
      </div>
    </div>
  );
}

// ─── Small reusable atoms ────────────────────────────────────────────────

function StatusBadge({ status }: { status: ProposalStatus }) {
  const color =
    status === 'Pending'  ? C.amber  :
    status === 'Accepted' ? C.neon   :
    status === 'Rejected' ? C.coral  :
                            C.violet;
  return (
    <span style={{
      fontSize: 10, fontFamily: F.mono, fontWeight: 600,
      color, background: `${color}15`, border: `1px solid ${color}40`,
      padding: '2px 8px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: '0.08em',
      flexShrink: 0,
    }}>
      {status}
    </span>
  );
}

function ConfidenceBar({ value, small = false }: { value: number; small?: boolean }) {
  const pct = Math.round(value * 100);
  const color = value >= 0.8 ? C.neon : value >= 0.6 ? C.amber : C.coral;
  const width = small ? 60 : 80;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width, height: small ? 4 : 6, background: C.faint, borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%', background: color,
          boxShadow: `0 0 4px ${color}80`,
        }} />
      </div>
      <span style={{
        fontSize: small ? 10 : 11, fontFamily: F.mono, color, minWidth: 28, textAlign: 'right',
      }}>
        {pct}%
      </span>
    </div>
  );
}

const contributionPillStyle = (count: number): CSSProperties => ({
  fontSize: 11, fontWeight: 700, fontFamily: F.mono,
  color: count >= 5 ? C.violet : count >= 2 ? C.cyan : C.muted,
  background: count >= 5 ? `${C.violet}18` : count >= 2 ? `${C.cyan}15` : 'transparent',
  border: `1px solid ${count >= 5 ? C.violet + '55' : count >= 2 ? C.cyan + '45' : C.faint}`,
  padding: '3px 10px', borderRadius: 12,
});

const headerButtonStyle: CSSProperties = {
  display: 'flex', gap: 14, alignItems: 'flex-start',
  padding: '14px 18px',
  width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
};

const primaryActionButtonStyle = (color: string): CSSProperties => ({
  padding: '7px 14px', fontSize: 12, fontWeight: 600, fontFamily: F.mono,
  background: `${color}22`, color, border: `1px solid ${color}`, borderRadius: 5,
  cursor: 'pointer',
});

const secondaryActionButtonStyle = (color: string): CSSProperties => ({
  padding: '7px 14px', fontSize: 12, fontWeight: 600, fontFamily: F.mono,
  background: 'transparent', color, border: `1px solid ${color}55`, borderRadius: 5,
  cursor: 'pointer',
});

function statusBorderColor(status: ProposalStatus): string {
  return status === 'Pending'  ? `${C.amber}55`  :
         status === 'Accepted' ? `${C.neon}55`   :
         status === 'Rejected' ? `${C.coral}40`  :
                                 `${C.violet}40`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Modals
// ═══════════════════════════════════════════════════════════════════════════

interface ProjectSummary { id: number; name: string }

function ModalShell({
  title,
  children,
  onClose,
}: {
  title:    string;
  children: ReactNode;
  onClose:  () => void;
}) {
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.panel, border: `1px solid ${C.faint}`, borderRadius: 10,
          padding: 22, width: 520, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
      >
        <h3 style={{ margin: 0, fontFamily: F.display, fontSize: 16, color: C.text }}>
          {title}
        </h3>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ─── Accept modal ───────────────────────────────────────────────────────

function AcceptProposalModal({
  proposal, projects, onClose, onDone,
}: {
  proposal: ProposalSummary;
  projects: ProjectSummary[];
  onClose:  () => void;
  onDone:   () => void;
}) {
  const { accept, loading, error } = useAcceptProposal();
  const [title,       setTitle]       = useState(proposal.title);
  const [description, setDescription] = useState(proposal.description ?? '');
  const [projectId,   setProjectId]   = useState<number | null>(proposal.suggestedProjectId);
  const [phaseName,   setPhaseName]   = useState(proposal.suggestedPhaseName ?? '');

  const valid = title.trim().length > 0 && title.length <= 200 && projectId != null;

  const submit = async () => {
    if (!valid) return;
    const result = await accept(proposal.id, {
      title:       title.trim(),
      description: description.trim() || undefined,
      projectId:   projectId!,
      phaseName:   phaseName.trim() || undefined,
    });
    if (result) onDone();
  };

  return (
    <ModalShell title={`Accept proposal: ${proposal.title}`} onClose={onClose}>
      <p style={modalHelpStyle}>
        Accepting creates a real WorkItem in the selected project. {proposal.contributionCount}{' '}
        {proposal.contributionCount === 1 ? 'conversation' : 'conversations'}{' '}
        contributing to this proposal will be re-mapped to the new WorkItem so auto-commit can
        pick them up. The proposal row itself becomes an immutable audit record.
      </p>

      <FormLabel>Title</FormLabel>
      <FormInput value={title} onChange={setTitle} maxLength={200} />
      <FormHint>{title.length}/200 — this becomes the WorkItem title.</FormHint>

      <FormLabel>Description</FormLabel>
      <FormTextarea value={description} onChange={setDescription} rows={4} />
      <FormHint>Optional but recommended — ≤2000 chars.</FormHint>

      <FormLabel>Project *</FormLabel>
      <FormSelect
        value={projectId == null ? '' : String(projectId)}
        onChange={v => setProjectId(v === '' ? null : Number(v))}
        options={[
          { value: '', label: '— select project —' },
          ...projects.map(p => ({ value: String(p.id), label: p.name })),
        ]}
      />

      <FormLabel>Phase</FormLabel>
      <FormInput value={phaseName} onChange={setPhaseName} maxLength={200} />
      <FormHint>
        Leave blank to drop into "Uncategorized". If a phase with this name exists in the
        project it is reused; otherwise a new phase is created.
      </FormHint>

      {error && <div style={modalErrorStyle}>{error.message}</div>}

      <ModalFooter>
        <button onClick={onClose} style={modalCancelButtonStyle} disabled={loading}>
          Cancel
        </button>
        <button onClick={submit} style={modalPrimaryButtonStyle(C.neon, !valid || loading)} disabled={!valid || loading}>
          {loading ? '◌ Creating…' : 'Accept & create WorkItem'}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ─── Reject modal ───────────────────────────────────────────────────────

function RejectProposalModal({
  proposal, onClose, onDone,
}: {
  proposal: ProposalSummary;
  onClose:  () => void;
  onDone:   () => void;
}) {
  const { reject, loading, error } = useRejectProposal();
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 10;

  const submit = async () => {
    if (!valid) return;
    const ok = await reject(proposal.id, { reason: reason.trim() });
    if (ok) onDone();
  };

  return (
    <ModalShell title={`Reject proposal: ${proposal.title}`} onClose={onClose}>
      <p style={modalHelpStyle}>
        Contributing conversations will NOT get a work item mapping — they remain in the
        Unmapped queue for manual handling. The rejection reason is preserved in the audit
        trail; be specific so future reviewers understand the decision.
      </p>
      <FormLabel>Rejection reason *</FormLabel>
      <FormTextarea value={reason} onChange={setReason} rows={5} />
      <FormHint>
        At least 10 characters. Example: "Scope belongs to work item #14, which was not in
        the catalog when this batch was imported but has since been created."
      </FormHint>

      {error && <div style={modalErrorStyle}>{error.message}</div>}

      <ModalFooter>
        <button onClick={onClose} style={modalCancelButtonStyle} disabled={loading}>
          Cancel
        </button>
        <button onClick={submit} style={modalPrimaryButtonStyle(C.coral, !valid || loading)} disabled={!valid || loading}>
          {loading ? '◌ Rejecting…' : 'Reject proposal'}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ─── Edit modal ─────────────────────────────────────────────────────────

function EditProposalModal({
  proposal, projects, onClose, onDone,
}: {
  proposal: ProposalSummary;
  projects: ProjectSummary[];
  onClose:  () => void;
  onDone:   () => void;
}) {
  const { edit, loading, error } = useEditProposal();
  const [title,       setTitle]       = useState(proposal.title);
  const [description, setDescription] = useState(proposal.description ?? '');
  const [projectId,   setProjectId]   = useState<number | null>(proposal.suggestedProjectId);
  const [phaseName,   setPhaseName]   = useState(proposal.suggestedPhaseName ?? '');

  const valid = title.trim().length > 0 && title.length <= 200;

  const submit = async () => {
    if (!valid) return;
    const ok = await edit(proposal.id, {
      title:       title.trim(),
      description: description.trim(),
      projectId:   projectId ?? undefined,
      phaseName:   phaseName.trim(),
    });
    if (ok) onDone();
  };

  return (
    <ModalShell title={`Edit proposal`} onClose={onClose}>
      <p style={modalHelpStyle}>
        Tidy up the AI's proposal before accepting. Changes are saved to the proposal row
        and used by subsequent Accept unless explicitly overridden in the Accept modal.
      </p>
      <FormLabel>Title</FormLabel>
      <FormInput value={title} onChange={setTitle} maxLength={200} />

      <FormLabel>Description</FormLabel>
      <FormTextarea value={description} onChange={setDescription} rows={4} />

      <FormLabel>Project</FormLabel>
      <FormSelect
        value={projectId == null ? '' : String(projectId)}
        onChange={v => setProjectId(v === '' ? null : Number(v))}
        options={[
          { value: '', label: '— no project —' },
          ...projects.map(p => ({ value: String(p.id), label: p.name })),
        ]}
      />

      <FormLabel>Phase</FormLabel>
      <FormInput value={phaseName} onChange={setPhaseName} maxLength={200} />

      {error && <div style={modalErrorStyle}>{error.message}</div>}

      <ModalFooter>
        <button onClick={onClose} style={modalCancelButtonStyle} disabled={loading}>
          Cancel
        </button>
        <button onClick={submit} style={modalPrimaryButtonStyle(C.cyan, !valid || loading)} disabled={!valid || loading}>
          {loading ? '◌ Saving…' : 'Save changes'}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ─── Merge modal ────────────────────────────────────────────────────────

function MergeProposalModal({
  source, candidates, onClose, onDone,
}: {
  source:     ProposalSummary;
  candidates: ProposalSummary[];
  onClose:    () => void;
  onDone:     () => void;
}) {
  const { merge, loading, error } = useMergeProposal();
  const [targetId, setTargetId] = useState<number | null>(null);

  // Only same-batch pending proposals are eligible targets. Server enforces
  // this too but surfacing it client-side keeps the dropdown honest.
  const eligible = candidates.filter(p => p.sourceBatchId === source.sourceBatchId);

  const submit = async () => {
    if (targetId == null) return;
    const result = await merge(source.id, targetId);
    if (result) onDone();
  };

  return (
    <ModalShell title={`Merge: ${source.title}`} onClose={onClose}>
      <p style={modalHelpStyle}>
        Move all {source.contributionCount} contributions from this proposal into the target
        proposal. This one flips to <strong>Merged</strong> for audit. Use when the AI
        produced near-duplicate proposals that should have been a single one.
      </p>

      <FormLabel>Merge into</FormLabel>
      {eligible.length === 0 ? (
        <div style={{
          fontSize: 12, color: C.muted, fontFamily: F.display,
          padding: '10px 12px', background: `${C.amber}10`,
          border: `1px solid ${C.amber}40`, borderRadius: 6,
        }}>
          No other pending proposals in the same batch are available to merge into.
        </div>
      ) : (
        <FormSelect
          value={targetId == null ? '' : String(targetId)}
          onChange={v => setTargetId(v === '' ? null : Number(v))}
          options={[
            { value: '', label: '— pick target proposal —' },
            ...eligible.map(p => ({
              value: String(p.id),
              label: `${p.title}  —  ${p.contributionCount} convo${p.contributionCount === 1 ? '' : 's'}  —  ${p.clusterKey}`,
            })),
          ]}
        />
      )}

      {error && <div style={modalErrorStyle}>{error.message}</div>}

      <ModalFooter>
        <button onClick={onClose} style={modalCancelButtonStyle} disabled={loading}>
          Cancel
        </button>
        <button
          onClick={submit}
          style={modalPrimaryButtonStyle(C.violet, targetId == null || loading)}
          disabled={targetId == null || loading}
        >
          {loading ? '◌ Merging…' : 'Merge'}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ─── Modal form atoms ───────────────────────────────────────────────────

function FormLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      fontSize: 11, fontFamily: F.mono, fontWeight: 600,
      color: C.muted, letterSpacing: '0.08em',
      textTransform: 'uppercase', marginTop: 14, marginBottom: 5,
    }}>
      {children}
    </div>
  );
}
function FormHint({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: C.dim, fontFamily: F.display, marginTop: 4 }}>
      {children}
    </div>
  );
}
function FormInput({
  value, onChange, maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
}) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      maxLength={maxLength}
      style={{
        width: '100%', padding: '8px 10px', fontSize: 13, fontFamily: F.display,
        background: C.bg, color: C.text,
        border: `1px solid ${C.faint}`, borderRadius: 6, outline: 'none',
      }}
    />
  );
}
function FormTextarea({
  value, onChange, rows = 4,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      style={{
        width: '100%', padding: '8px 10px', fontSize: 12, fontFamily: F.display,
        background: C.bg, color: C.text,
        border: `1px solid ${C.faint}`, borderRadius: 6,
        resize: 'vertical', lineHeight: 1.5, outline: 'none',
      }}
    />
  );
}
function FormSelect({
  value, onChange, options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', padding: '8px 10px', fontSize: 13, fontFamily: F.display,
        background: C.bg, color: C.text,
        border: `1px solid ${C.faint}`, borderRadius: 6, outline: 'none',
      }}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
      {children}
    </div>
  );
}

// ─── Shared styles ──────────────────────────────────────────────────────

const filterPillStyle = (active: boolean, color: string): CSSProperties => ({
  padding:      '6px 14px',
  fontSize:     12,
  fontWeight:   600,
  fontFamily:   F.display,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color:        active ? color : C.muted,
  background:   active ? `${color}14` : 'transparent',
  border:       `1px solid ${active ? color + '58' : C.faint}`,
  borderRadius: 6,
  cursor:       'pointer',
  transition:   'all 0.15s ease',
});

const batchPillStyle: CSSProperties = {
  padding:      '6px 12px',
  fontSize:     11,
  color:        C.amber,
  background:   `${C.amber}08`,
  border:       `1px solid ${C.amber}28`,
  borderRadius: 6,
  fontFamily:   F.display,
};

const refreshButtonStyle: CSSProperties = {
  width:        32,
  height:       32,
  padding:      0,
  fontSize:     16,
  background:   'transparent',
  color:        C.muted,
  border:       `1px solid ${C.faint}`,
  borderRadius: 6,
  cursor:       'pointer',
};

const errorBoxStyle: CSSProperties = {
  padding:      '16px',
  background:   `${C.coral}08`,
  border:       `1px solid ${C.coral}38`,
  borderRadius: 8,
  color:        C.coral,
  fontSize:     13,
  fontFamily:   F.display,
};

const modalHelpStyle: CSSProperties = {
  fontSize: 12, color: C.muted, lineHeight: 1.55, marginTop: 10, fontFamily: F.display,
};
const modalErrorStyle: CSSProperties = {
  marginTop: 14, fontSize: 12, color: C.coral, fontFamily: F.display,
};
const modalCancelButtonStyle: CSSProperties = {
  padding: '7px 14px', borderRadius: 6, fontSize: 12, fontFamily: F.mono,
  background: 'transparent', cursor: 'pointer',
  border: `1px solid ${C.faint}`, color: C.muted,
};
const modalPrimaryButtonStyle = (color: string, disabled: boolean): CSSProperties => ({
  padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: F.mono,
  background: `${color}22`, color,
  border: `1px solid ${color}`, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});
